import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /** @type {HTMLElement | null} */
  #loadingOverlay = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    const lineItemRow = this.refs.cartItemRows[line - 1];
    const quantityInput = /** @type {HTMLInputElement | null} */ (lineItemRow?.querySelector('input[type="number"]'));
    const previousQuantity = Number(quantityInput?.defaultValue || quantityInput?.getAttribute('value') || 0);

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    // Keep separate-line-item behavior on cart page:
    // increasing quantity adds new unique line items instead of changing this line's quantity.
    if (!lineItemRow?.dataset.bundleId && quantity > previousQuantity) {
      const quantityToAdd = quantity - previousQuantity;

      if (quantityInput) {
        quantityInput.value = String(previousQuantity);
      }

      this.#addAsSeparateLineItems(line, quantityToAdd);
      return;
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Adds quantity as separate line items by cloning the source line's variant/properties
   * and attaching unique private properties so Shopify does not merge them.
   * @param {number} line
   * @param {number} quantityToAdd
   */
  /**
   * @param {Record<string, string | number | null | undefined>} sourceProperties
   * @returns {Record<string, string>}
   */
  #getVisibleCartProperties(sourceProperties) {
    /** @type {Record<string, string>} */
    const visibleProperties = {};

    Object.entries(sourceProperties).forEach(([propertyKey, propertyValue]) => {
      if (propertyValue == null || propertyKey.startsWith('_')) return;
      visibleProperties[propertyKey] = String(propertyValue);
    });

    return visibleProperties;
  }

  /**
   * @param {any} sourceItem
   * @param {number} quantityToAdd
   * @returns {Array<{ id: number; quantity: number; properties: Record<string, string>; selling_plan?: number }>}
   */
  #buildSeparateCartLineItems(sourceItem, quantityToAdd) {
    const visibleProperties = this.#getVisibleCartProperties(sourceItem.properties || {});
    const sellingPlanId = sourceItem.selling_plan_allocation?.selling_plan?.id;

    return Array.from({ length: quantityToAdd }, (_, index) => {
      const uniqueBase = `cart_${sourceItem.id}_${Date.now()}_${(performance.now() * 1000).toFixed(0)}_${Math.random()
        .toString(36)
        .slice(2, 12)}_${index}`;

      const properties = {
        ...visibleProperties,
        _unique_id: uniqueBase,
        _line_item_id: `${uniqueBase}_line`,
        _item_index: String(index),
      };

      return sellingPlanId
        ? { id: sourceItem.id, quantity: 1, properties, selling_plan: sellingPlanId }
        : { id: sourceItem.id, quantity: 1, properties };
    });
  }

  async #addAsSeparateLineItems(line, quantityToAdd) {
    if (quantityToAdd <= 0) return;

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('add:user-action');
    this.#disableCartItems();

    const { cartTotal } = this.refs;
    cartTotal?.shimmer();

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    /** @type {Record<string, string> | undefined} */
    let latestSections;

    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      const sourceItem = cart.items?.[line - 1];

      if (!sourceItem) throw new Error('Cart line item not found');

      const sectionsParam = Array.from(sectionsToUpdate).join(',');
      const items = this.#buildSeparateCartLineItems(sourceItem, quantityToAdd);

      const fetchCfg = fetchConfig('json', {
        body: JSON.stringify({
          items,
          sections: sectionsParam,
          sections_url: window.location.pathname,
        }),
      });
      const response = await fetch(Theme.routes.cart_add_url, fetchCfg);
      const responseData = await response.json();

      if (responseData?.status) {
        throw new Error(responseData.message || responseData.description || 'Failed to add item to cart');
      }

      if (responseData?.sections) {
        latestSections = responseData.sections;
      }

      let newCartItemCount = 0;
      const updatedSectionHtml = latestSections?.[this.sectionId];
      if (updatedSectionHtml) {
        const newSectionHTML = new DOMParser().parseFromString(updatedSectionHtml, 'text/html');
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;
      } else {
        const latestCartResponse = await fetch('/cart.js');
        const latestCart = await latestCartResponse.json();
        newCartItemCount = Number(latestCart.item_count) || 0;
      }

      this.dispatchEvent(
        new CartUpdateEvent({}, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: latestSections,
        })
      );

      const updatedSectionHtmlForMorph = latestSections?.[this.sectionId];
      if (updatedSectionHtmlForMorph) {
        morphSection(this.sectionId, updatedSectionHtmlForMorph);
      } else {
        sectionRenderer.renderSection(this.sectionId, { cache: false });
      }
    } catch (error) {
      console.error(error);
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    } finally {
      resetShimmer(this);
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    // If this is part of a bundle, remove all rows with the same bundle id
    const bundleId = cartItemRowToRemove.dataset.bundleId;
    const bundleRows = bundleId
      ? this.refs.cartItemRows.filter((row) => row.dataset.bundleId === bundleId)
      : [];

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
      // Include sibling bundle rows, if any
      ...bundleRows.filter((row) => row !== cartItemRowToRemove),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const targetRow = this.refs.cartItemRows[line - 1];
    const bundleId = targetRow?.dataset?.bundleId;

    /**
     * If the target line is part of a bundle, update all lines in the bundle together
     */
    if (bundleId) {
      /** @type {Record<string, number>} */
      const updatesMap = {};

      // We treat bundle updates as absolute: all lines in the bundle are set to the same requested quantity
      const isClear = quantity === 0 || config.action === 'clear';

      // Build updates for all rows with the same bundle id
      // Prefer explicit bundle keys when available
      const targetBundleKeysAttr = targetRow?.dataset?.bundleKeys || '';
      const explicitBundleKeys = targetBundleKeysAttr.split(',').map((k) => k.trim()).filter(Boolean);

      if (explicitBundleKeys.length > 0) {
        explicitBundleKeys.forEach((key) => {
          updatesMap[key] = isClear ? 0 : quantity;
        });
      } else {
        this.refs.cartItemRows.forEach((row) => {
          if (row?.dataset?.bundleId !== bundleId) return;
          const itemKey = row?.dataset?.key;
          if (itemKey) updatesMap[itemKey] = isClear ? 0 : quantity;
        });
      }

      const body = JSON.stringify({
        updates: updatesMap,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      });

      cartTotal?.shimmer();

      fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', { body }))
        .then((response) => {
          return response.text();
        })
        .then((responseText) => {
          const parsedResponseText = JSON.parse(responseText);

          resetShimmer(this);

          if (parsedResponseText.errors) {
            this.#handleCartError(line, parsedResponseText);
            return;
          }

          const newSectionHTML = new DOMParser().parseFromString(
            parsedResponseText.sections[this.sectionId],
            'text/html'
          );

          const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
          const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

          this.dispatchEvent(
            new CartUpdateEvent({}, this.sectionId, {
              itemCount: newCartItemCount,
              source: 'cart-items-component',
              sections: parsedResponseText.sections,
            })
          );

          morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

          // After quantity sync, ensure bundle properties are present on all lines
          this.#ensureBundleProperties(bundleId, Object.keys(updatesMap));
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          this.#enableCartItems();
          cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
        });

      return;
    }

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Ensures all lines in a bundle share the same properties (e.g., _bundle_id)
   * Copies properties from the first available line to the rest using change.js
   * @param {string} bundleId
   * @param {string[]} bundleKeys
   */
  async #ensureBundleProperties(bundleId, bundleKeys) {
    try {
      if (!bundleId || !Array.isArray(bundleKeys) || bundleKeys.length === 0) return;

      const cartResp = await fetch('/cart.js');
      const cart = await cartResp.json();
      const byKey = new Map();
      for (const item of cart.items || []) {
        if (bundleKeys.includes(item.key)) byKey.set(item.key, item);
      }

      // Choose source properties from the first item
      const firstKey = bundleKeys.find((k) => byKey.has(k));
      if (!firstKey) return;
      const sourceItem = byKey.get(firstKey);
      const sourceProps = sourceItem?.properties || {};

      // If source doesn't have _bundle_id, add it
      if (bundleId && (!sourceProps || sourceProps._bundle_id !== bundleId)) {
        sourceProps._bundle_id = bundleId;
      }

      const changes = [];
      for (const key of bundleKeys) {
        const target = byKey.get(key);
        const targetProps = target?.properties || {};
        const needsCopy = JSON.stringify(targetProps) !== JSON.stringify(sourceProps);
        if (needsCopy) {
          changes.push(
            fetch('/cart/change.js', fetchConfig('json', {
              body: JSON.stringify({ id: key, properties: sourceProps }),
            }))
          );
        }
      }

      if (changes.length > 0) {
        await Promise.allSettled(changes);
        // Trigger a section refresh after properties sync
        sectionRenderer.renderSection(this.sectionId, { cache: false });
      }
    } catch (err) {
      console.error('Failed to ensure bundle properties', err);
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
    this.#showLoadingOverlay();
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
    this.#hideLoadingOverlay();
  }

  #showLoadingOverlay() {
    if (!this.#loadingOverlay) {
      const overlay = document.createElement('div');
      overlay.className = 'cart-loading-overlay';
      overlay.setAttribute('aria-hidden', 'true');
      overlay.innerHTML = `
        <div class="cart-loading-spinner">
          <div class="spinner"></div>
        </div>
      `;
      this.#loadingOverlay = overlay;
    }

    if (!this.#loadingOverlay.isConnected) {
      this.appendChild(this.#loadingOverlay);
    }

    this.classList.add('cart-items--loading');
  }

  #hideLoadingOverlay() {
    this.#loadingOverlay?.remove();
    this.classList.remove('cart-items--loading');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
